/**
 * Skill CRUD operations — create, update, delete, query AI skills.
 *
 * Skills are domain knowledge templates stored in the `plan` table
 * with kind="skill". They guide AI plan generation via system prompts,
 * constraints, and example steps.
 */

import { getDb } from "./runtime.js"
import { ok, fail } from "./op-types.js"
import type { OpResult } from "./op-types.js"
import type { DbSkill, SkillConstraints, SkillExampleStep, PackQuestion } from "./planTypes.js"

// ---------------------------------------------------------------------------
// addSkill
// ---------------------------------------------------------------------------

export interface AddSkillOpts {
  name: string
  category: string
  systemPrompt: string
  constraints: SkillConstraints
  exampleSteps: SkillExampleStep[]
  description?: string
  icon?: string
  questions?: PackQuestion[]
  sourcePackId?: string
  locale?: string
  isPublished?: boolean
}

export async function addSkill(
  opts: AddSkillOpts,
): Promise<OpResult<{ id: string }>> {
  const db = getDb()
  try {
    const id = crypto.randomUUID()
    const skill: DbSkill = {
      id,
      kind: "skill",
      name: opts.name,
      description: opts.description,
      icon: opts.icon,
      category: opts.category,
      systemPrompt: opts.systemPrompt,
      constraints: opts.constraints,
      exampleSteps: opts.exampleSteps,
      questions: opts.questions,
      sourcePackId: opts.sourcePackId,
      locale: opts.locale,
      version: 1,
      isBuiltIn: false,
      isPublished: opts.isPublished ?? false,
      creatorId: db.cloud.currentUserId,
      owner: db.cloud.currentUserId,
    }
    await db.plan.add(skill)
    return ok({ id })
  } catch (e) {
    return fail(`addSkill failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// updateSkill
// ---------------------------------------------------------------------------

export interface UpdateSkillOpts {
  name?: string
  description?: string
  icon?: string
  category?: string
  systemPrompt?: string
  constraints?: SkillConstraints
  exampleSteps?: SkillExampleStep[]
  questions?: PackQuestion[]
  isPublished?: boolean
}

export async function updateSkill(
  id: string,
  updates: UpdateSkillOpts,
): Promise<OpResult<void>> {
  const db = getDb()
  try {
    const existing = await db.plan.get(id)
    if (!existing || existing.kind !== "skill") return fail("Skill not found")

    const skill = existing as DbSkill
    // Cast needed: update() sees the union type and doesn't know `version` exists on all branches
    await (db.plan as import("dexie").Table<DbSkill, string>).update(id, {
      ...updates,
      version: skill.version + 1,
    })
    return ok(undefined)
  } catch (e) {
    return fail(`updateSkill failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// deleteSkill
// ---------------------------------------------------------------------------

export async function deleteSkill(id: string): Promise<OpResult<void>> {
  const db = getDb()
  try {
    const existing = await db.plan.get(id)
    if (!existing || existing.kind !== "skill") return fail("Skill not found")
    await db.plan.delete(id)
    return ok(undefined)
  } catch (e) {
    return fail(`deleteSkill failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// getSkill
// ---------------------------------------------------------------------------

export async function getSkill(id: string): Promise<OpResult<DbSkill>> {
  const db = getDb()
  try {
    const entry = await db.plan.get(id)
    if (!entry || entry.kind !== "skill") return fail("Skill not found")
    return ok(entry as DbSkill)
  } catch (e) {
    return fail(`getSkill failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// listSkills
// ---------------------------------------------------------------------------

export interface ListSkillsOpts {
  category?: string
  builtInOnly?: boolean
}

export async function listSkills(
  opts?: ListSkillsOpts,
): Promise<OpResult<DbSkill[]>> {
  const db = getDb()
  try {
    let collection = db.plan.where("kind").equals("skill")

    const skills = (await collection.toArray()) as DbSkill[]
    let filtered = skills

    if (opts?.category) {
      filtered = filtered.filter((s) => s.category === opts.category)
    }
    if (opts?.builtInOnly) {
      filtered = filtered.filter((s) => s.isBuiltIn)
    }

    return ok(filtered)
  } catch (e) {
    return fail(`listSkills failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}
